import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";

const FORM_URL = "https://readdy.ai/api/form/d6u3e21qcrg2vc5aq0pg";

export default function FinalCTA() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((val, key) => {
      data.append(key, val.toString());
    });
    try {
      await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
      formRef.current?.reset();
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-24 bg-[#1A1A1A]" id="proposal">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <span className="inline-block text-[#C9A961] text-xs font-semibold uppercase tracking-widest mb-4">
              Start Today
            </span>
            <h2
              className="text-4xl lg:text-5xl font-semibold text-white leading-tight mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Ready to Bring Your Space Together?
            </h2>
            <p className="text-white/55 leading-relaxed mb-8 max-w-md">
              Get a clear, professional design plan tailored to your style and budget. No guesswork, no overwhelm — just a structured path to the space you&apos;ve been envisioning.
            </p>
            <div className="space-y-4 mb-10">
              {[
                { icon: "ri-check-double-line", text: "Proposals delivered in 5–14 business days" },
                { icon: "ri-check-double-line", text: "Budget-aligned from the first recommendation" },
                { icon: "ri-check-double-line", text: "Fully remote — available in all 50 states" },
                { icon: "ri-check-double-line", text: "Contractor-ready documentation included" },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center text-[#C9A961] shrink-0">
                    <i className={`${item.icon} text-base`}></i>
                  </div>
                  <span className="text-white/70 text-sm">{item.text}</span>
                </div>
              ))}
            </div>
            <a
              href="tel:+18005550199"
              className="whitespace-nowrap inline-flex items-center gap-2 border border-white/20 text-white font-medium px-7 py-3.5 rounded-md hover:border-[#C9A961] hover:text-[#C9A961] transition-all duration-300 cursor-pointer text-sm"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line"></i>
              </div>
              Call (800) 555-0199
            </a>
          </div>

          <div className="bg-white rounded-2xl p-8">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 flex items-center justify-center bg-[#C9A961]/15 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-[#C9A961] text-2xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-[#111111] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Request Received!
                </h3>
                <p className="text-[#5C5C5C] text-sm">
                  Thank you for reaching out. We&apos;ll be in touch within 1 business day to discuss your project.
                </p>
              </div>
            ) : (
              <>
                <h3
                  className="text-xl font-semibold text-[#111111] mb-1"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Get Your Design Proposal
                </h3>
                <p className="text-sm text-[#5C5C5C] mb-6">We&apos;ll be in touch within 1 business day.</p>

                <form
                  id="homepage-proposal-form"
                  ref={formRef}
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-4"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-[#111111] mb-1.5">First Name</label>
                      <input
                        type="text"
                        name="first_name"
                        required
                        placeholder="Jane"
                        className="w-full border border-[#E8E3DC] rounded-md px-3 py-2.5 text-sm text-[#111111] placeholder-[#B0A99F] focus:outline-none focus:border-[#C9A961] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#111111] mb-1.5">Last Name</label>
                      <input
                        type="text"
                        name="last_name"
                        required
                        placeholder="Smith"
                        className="w-full border border-[#E8E3DC] rounded-md px-3 py-2.5 text-sm text-[#111111] placeholder-[#B0A99F] focus:outline-none focus:border-[#C9A961] transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#111111] mb-1.5">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="jane@example.com"
                      className="w-full border border-[#E8E3DC] rounded-md px-3 py-2.5 text-sm text-[#111111] placeholder-[#B0A99F] focus:outline-none focus:border-[#C9A961] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#111111] mb-1.5">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="(555) 000-0000"
                      className="w-full border border-[#E8E3DC] rounded-md px-3 py-2.5 text-sm text-[#111111] placeholder-[#B0A99F] focus:outline-none focus:border-[#C9A961] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#111111] mb-1.5">Service Interested In</label>
                    <select
                      name="service"
                      required
                      className="w-full border border-[#E8E3DC] rounded-md px-3 py-2.5 text-sm text-[#111111] focus:outline-none focus:border-[#C9A961] transition-colors bg-white"
                    >
                      <option value="">Select a service…</option>
                      <option value="Interior Design Proposals">Interior Design Proposals</option>
                      <option value="Renovation Planning">Renovation Planning &amp; Layout Design</option>
                      <option value="Material & Finish Selection">Material &amp; Finish Selection</option>
                      <option value="Room-by-Room Packages">Room-by-Room Design Packages</option>
                      <option value="Virtual Consultations">Virtual Design Consultations</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#111111] mb-1.5">Tell Us About Your Project</label>
                    <textarea
                      name="message"
                      rows={3}
                      maxLength={500}
                      placeholder="Briefly describe your space, goals, and timeline…"
                      className="w-full border border-[#E8E3DC] rounded-md px-3 py-2.5 text-sm text-[#111111] placeholder-[#B0A99F] focus:outline-none focus:border-[#C9A961] transition-colors resize-none"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="whitespace-nowrap w-full bg-[#C9A961] text-white font-semibold py-3.5 rounded-md hover:bg-[#b8924d] transition-colors duration-300 cursor-pointer text-sm disabled:opacity-60"
                  >
                    {submitting ? "Sending…" : "Submit Design Proposal Request"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
